import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import EmailConfirmModal from './EmailConfirmModal';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const RESEND_ROLES = ['admin', 'tax_search_team_leader', 'tax_processor', 'processor', 'team_leader'];

/* Header action — same endpoint, roles and confirmation flow the old Fulfillment Delivery Status panel used. */
export default function ResendTaxCertificateButton({ orderId, orderStatus, userRole, onSent }) {
  const [info, setInfo] = useState(null);
  const [external, setExternal] = useState(null);
  const [resending, setResending] = useState(false);
  const [open, setOpen] = useState(false);
  const inFlight = useRef(false);

  const isCompleted = orderStatus === 'complete' || orderStatus === 'completed';
  const canResend = RESEND_ROLES.includes((userRole || '').toLowerCase());

  const fetchInfo = useCallback(async () => {
    if (!orderId || !isCompleted || !canResend) return;
    try {
      const [i, p] = await Promise.all([
        axios.get(`${API}/api/orders/${orderId}/delivery/info`, auth()),
        axios.get(`${API}/api/orders/${orderId}/external-portal-status`, auth()).catch(() => null),
      ]);
      setInfo(i.data);
      setExternal(!!p?.data?.is_external_portal);
    } catch (err) { console.error('Failed to fetch delivery info:', err); }
  }, [orderId, isCompleted, canResend]);

  useEffect(() => { fetchInfo(); }, [fetchInfo]);

  const handleConfirmed = async (recipientEmail) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setResending(true);
    try {
      const payload = recipientEmail?.trim() ? { recipient_email: recipientEmail.trim() } : {};
      const res = await axios.post(`${API}/api/orders/${orderId}/delivery/resend`, payload, auth());
      toast.success(res.data.message || 'Tax certificate resent successfully');
      setOpen(false);
      await fetchInfo();
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend');
    } finally {
      setResending(false);
      inFlight.current = false;
    }
  };

  if (!isCompleted || !canResend || !info || external) return null;
  const status = info.delivery_status || 'pending';
  if (!['sent', 'failed', 'pending'].includes(status)) return null;
  const label = status === 'pending' ? 'Send Tax Certificate' : 'Resend Tax Certificate';

  return (
    <>
      <button type="button" onClick={() => !resending && setOpen(true)} disabled={resending} aria-busy={resending} title={label}
        className="odh-pill odh-pill-btn focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        data-testid="resend-delivery-btn">
        {resending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        <span className="hidden sm:inline">{label}</span><span className="sm:hidden">{status === 'pending' ? 'Send' : 'Resend'}</span>
      </button>
      <EmailConfirmModal open={open} onOpenChange={(o) => !resending && setOpen(o)}
        initialEmail={info.delivery_recipient || ''} knownEmail={info.client_config_delivery_email || info.delivery_recipient || ''}
        title={status === 'pending' ? 'Confirm & Send Tax Certificate' : 'Confirm & Resend Tax Certificate'}
        description="The tax certificate PDF will be emailed to the address below. Verify before sending — this triggers a real email."
        confirmLabel={status === 'pending' ? 'Confirm & Send' : 'Confirm & Resend'} busyLabel={status === 'pending' ? 'Sending…' : 'Resending…'}
        busy={resending} onConfirm={handleConfirmed} />
    </>
  );
}
